import React, { forwardRef } from "react";
import { Dimensions, ScrollView, StyleSheet, View } from "react-native";
// The package ships no typed default export, so the resolver flags this import
// even though it is correct. Same import as Returns/components/BottomSheet.
// eslint-disable-next-line import/default
import RBSheet from "react-native-raw-bottom-sheet";
import Text from "@components/CustomText";
import PrimaryButton from "@components/PrimaryButton";
import color from "@constants/color";
import CheckGreen from "@assets/svg/CheckGreen";
import CrossCircle from "@assets/svg/CrossCircle";
import {
  formatGigDayLabel,
  formatTimeLabel,
  getSkippedLockedCount,
  localTodayIso,
} from "../utils";
import type { GigsAvailabilitySubmitResponse } from "../types";
import { GIGS_COPY } from "../copy";

interface ConfirmationSheetProps {
  /** Renders straight off the POST response — never local state. Null while nothing has been submitted yet. */
  response: GigsAvailabilitySubmitResponse | null;
  onChange: () => void;
  onGotIt: () => void;
}

/**
 * Screen 3 — confirmation bottom sheet. Precedent for the RBSheet
 * ref-controlled open()/close() pattern: `src/screens/Returns/components/BottomSheet.tsx`.
 */
// Tall enough for the recap on a normal phone, but never more than 90% of a
// short screen — otherwise the footer buttons fall off the bottom and the
// sheet cannot be dismissed.
const SHEET_HEIGHT = Math.min(
  600,
  Math.round(Dimensions.get("window").height * 0.9),
);

const ConfirmationSheet = forwardRef<RBSheet, ConfirmationSheetProps>(
  ({ response, onChange, onGotIt }, ref) => {
    const days = response?.days ?? [];
    const todayIso = localTodayIso();
    const skippedCount = getSkippedLockedCount(response?.skipped_locked);
    const skippedNote =
      skippedCount === 0
        ? null
        : skippedCount === 1
          ? GIGS_COPY.confirmation.skippedSingular
          : GIGS_COPY.confirmation.skippedPlural(skippedCount);
    // No caption while the cutoff is off (the server sends no lock_time).
    const lockTimeLabel = formatTimeLabel(response?.duty?.lock_time);
    const sheetCaption = lockTimeLabel
      ? GIGS_COPY.confirmation.captionWithTime(lockTimeLabel)
      : null;

    return (
      <RBSheet
        ref={ref}
        animationType="slide"
        closeOnPressMask={false}
        height={SHEET_HEIGHT}
        customStyles={{
          container: styles.sheetContainer,
          wrapper: styles.sheetWrapper,
        }}
      >
        <ScrollView
          style={styles.body}
          contentContainerStyle={styles.bodyContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.header}>
            <CheckGreen size={48} />
            <Text variant="heading_24" style={styles.title}>
              {GIGS_COPY.confirmation.title}
            </Text>
            <Text variant="body_text_2" style={styles.subtitle}>
              {GIGS_COPY.confirmation.subtitle}
            </Text>
          </View>

          <View style={styles.recap}>
            {days.map((day) => (
              <View key={day.gig_date} style={styles.recapRow}>
                {day.response === "YES" ? (
                  <CheckGreen size={20} />
                ) : (
                  <CrossCircle width={20} height={20} />
                )}
                <Text variant="body_text_2" style={styles.recapLabel}>
                  {formatGigDayLabel(day.gig_date, todayIso)}
                </Text>
                <Text variant="title_16" style={styles.recapAnswer}>
                  {day.response === "YES"
                    ? GIGS_COPY.marking.yes
                    : GIGS_COPY.marking.no}
                </Text>
              </View>
            ))}
          </View>

          {skippedNote ? (
            <Text variant="caption_2" style={styles.skippedNote}>
              {skippedNote}
            </Text>
          ) : null}

          {sheetCaption ? (
            <Text variant="caption_2" style={styles.caption}>
              {sheetCaption}
            </Text>
          ) : null}
        </ScrollView>

        <View style={styles.footer}>
          <PrimaryButton
            testID="gigs-sheet-change-button"
            text={GIGS_COPY.confirmation.change}
            onPress={onChange}
            buttonColor={color.white}
            buttonStyle={styles.changeButton}
            textStyle={styles.changeButtonText}
          />
          <PrimaryButton
            testID="gigs-sheet-got-it-button"
            text={GIGS_COPY.confirmation.gotIt}
            onPress={onGotIt}
            buttonColor={color.black}
            buttonStyle={styles.gotItButton}
          />
        </View>
      </RBSheet>
    );
  },
);

ConfirmationSheet.displayName = "ConfirmationSheet";

export default ConfirmationSheet;

const styles = StyleSheet.create({
  sheetWrapper: {
    backgroundColor: color.modalBackground,
  },
  sheetContainer: {
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 16,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  body: {
    flexGrow: 0,
    flexShrink: 1,
  },
  bodyContent: {
    paddingBottom: 4,
  },
  header: {
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    color: color.mediumBlack,
    marginTop: 12,
  },
  subtitle: {
    color: color.lightMediumBlack,
    marginTop: 4,
  },
  recap: {
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: color.lineColor,
    paddingVertical: 8,
  },
  recapRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    gap: 10,
  },
  recapLabel: {
    flex: 1,
    color: color.mediumBlack,
  },
  recapAnswer: {
    color: color.mediumBlack,
  },
  skippedNote: {
    color: color.lightMediumBlack,
    marginTop: 10,
  },
  caption: {
    color: color.lightMediumBlack,
    marginTop: 10,
  },
  footer: {
    flexDirection: "row",
    gap: 12,
    marginTop: 20,
  },
  changeButton: {
    flex: 1,
    minWidth: 0,
    backgroundColor: color.white,
    borderWidth: 1,
    borderColor: color.mediumBlack,
    paddingVertical: 12,
  },
  changeButtonText: {
    color: color.mediumBlack,
  },
  gotItButton: {
    flex: 1,
    minWidth: 0,
    paddingVertical: 12,
    backgroundColor: color.black,
  },
});
